import { useUser } from "@clerk/clerk-react";
import { useEffect, useState, useRef, useCallback } from "react";
import debounce from "lodash.debounce";
import moment from "moment";
import { BlockBlobClient } from "@azure/storage-blob";
import { getBlobSasToken } from "../api/api-methods";

const DEBOUNCE_DELAY = 200;
const ROLE_DEBOUNCE_DELAY = 250; // Debounce role changes by 250ms
const LAST_KNOWN_ROLE_KEY = "lastKnownRole";

export const useDebouncedEffect = (callback, dependencies, delay = 500) => {
  useEffect(() => {
    const debouncedCallback = debounce(callback, delay);
    debouncedCallback();

    return () => {
      debouncedCallback.cancel();
    };
  }, dependencies);
};

/**
 * Enhanced useUserRole hook with debouncing and fallback
 *
 * Features:
 * - Debounces role changes by 250ms to prevent UI flicker during role switch
 * - Falls back to localStorage "lastKnownRole" when role is undefined/null
 * - Persists valid roles to localStorage for instant fallback
 * - Returns stableRole instead of raw role to prevent blank screens
 *
 * @returns {string|null} The stable, debounced user role
 */
export const useUserRole = () => {
  const { user } = useUser();

  // Get initial role from localStorage as fallback
  const getInitialRole = () => {
    const lastKnown = localStorage.getItem(LAST_KNOWN_ROLE_KEY);
    return lastKnown || null;
  };

  const [stableRole, setStableRole] = useState(getInitialRole);
  const pendingRoleRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Debounced role updater - prevents UI flicker during role switch
  const updateRole = useCallback((newRole) => {
    // Clear any pending debounce
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    pendingRoleRef.current = newRole;

    debounceTimerRef.current = setTimeout(() => {
      if (pendingRoleRef.current) {
        setStableRole(pendingRoleRef.current);
        // Persist valid roles to localStorage for fallback
        localStorage.setItem(LAST_KNOWN_ROLE_KEY, pendingRoleRef.current);
      }
    }, ROLE_DEBOUNCE_DELAY);
  }, []);

  useEffect(() => {
    const rawRole = user?.publicMetadata?.role;

    if (rawRole && rawRole !== "Unknown Role") {
      // Valid role received - debounce the update
      updateRole(rawRole);
    } else if (!rawRole && !stableRole) {
      // No role and no stable role - try localStorage fallback
      const lastKnown = localStorage.getItem(LAST_KNOWN_ROLE_KEY);
      if (lastKnown) {
        setStableRole(lastKnown);
      }
    }
    // If rawRole is undefined but we have stableRole, keep showing stableRole
    // This prevents blank screen during Clerk metadata refresh

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [user, updateRole, stableRole]);

  return stableRole;
};

/**
 * Hook to get both raw and stable role (useful for debugging)
 */
export const useUserRoleDebug = () => {
  const { user } = useUser();
  const stableRole = useUserRole();
  const rawRole = user?.publicMetadata?.role;

  return {
    rawRole,
    stableRole,
    isStale: rawRole !== stableRole,
    lastKnown: localStorage.getItem(LAST_KNOWN_ROLE_KEY),
  };
};

export const dateFormat = (date) => {
  const formatted = moment.utc(date).format("MMMM D, YYYY");
  return formatted;
};

export const calculateDaysAgo = (dateString) => {
  const daysAgo = moment().diff(moment(dateString), "days");

  if (daysAgo === 0) return "Today";
  return `${daysAgo}d ago`;
};

export const uploadFile = async (file, guid, entityType) => {
  const selectedFile = file;

  if (!selectedFile) {
    return;
  }

  try {
    // Fetch the SAS token and other required data
    const blobData = await getBlobSasToken(entityType, guid);

    const { storage_account_name, container_name, blob_path, sas_token } =
      blobData;

    // Validate blobData to check if required properties exist
    if (!storage_account_name || !container_name || !sas_token) {
      console.error("Missing required blob data:", blobData);
      return;
    }

    const blobUrl = `https://${storage_account_name}.blob.core.windows.net/${container_name}/${selectedFile.name}?${sas_token}`;

    const blobClient = new BlockBlobClient(blobUrl);

    // Upload the file
    await blobClient.uploadData(selectedFile);

    return blobUrl;
  } catch (error) {
    console.error("Error uploading file:", error);
    alert("An error occurred during upload. Please try again.");
    return null; // Ensure the function always returns something, even in case of error
  }
};

// export const getCityStateCountry = async (address) => {
//   const API_KEY = "process.env.REACT_APP_GOOGLE_MAPS_API_KEY";
//   if (!API_KEY) {
//     console.error("Google Maps API key is missing!");
//     return null;
//   }

//   const encodedAddress = encodeURIComponent(address);
//   const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodedAddress}&key=${API_KEY}`;

//   try {
//     const response = await fetch(url);
//     const data = await response.json();

//     if (data.status !== "OK" || !data.results.length) {
//       console.error("Geocoding failed:", data.status);
//       return null;
//     }

//     const addressComponents = data.results[0].address_components;

//     let city = "";
//     let state = "";
//     let country = "";

//     addressComponents.forEach((component) => {
//       if (
//         component.types.includes("locality") ||
//         component.types.includes("administrative_area_level_2")
//       ) {
//         city = component.long_name;
//       } else if (component.types.includes("administrative_area_level_1")) {
//         state = component.long_name;
//       } else if (component.types.includes("country")) {
//         country = component.long_name;
//       }
//     });

//     return { city, state, country };
//   } catch (error) {
//     console.error("Error fetching location data:", error);
//     return null;
//   }
// };

export const formatUserDetails = (details) => {
  if (!details) return null;
  return {
    id: details.id,
    name: details.full_name,
    email: details.email,
    phone: details.phone_number || details.office_phone_number,
    first_Name: details.first_name,
    last_Name: details.last_name,
    created_Date: details.create_date_time,
    updated_Date: details.update_date_time,
    status: details.is_active ? "Active" : "Inactive",
    sponsorId: details.sponsor_id,
    contact_Preference: details.contact_preference,
  };
};
const today = new Date();
export const twoWeeks = new Date();
twoWeeks.setDate(today.getDate() + 14);

export const fortyFiveDays = new Date();
fortyFiveDays.setDate(today.getDate() + 45);

export const dateFormatRFi = (date) => {
  return moment.utc(date).format("MMM YYYY");
};

export const userHasRight = (rights, rightName) => {
  return rights.includes(rightName);
};

export { canRequestDocumentLibraryAccess } from "./documentLibraryRequestAccess";

/**
 * Returns true if the user is considered an admin (view-only: can see Discovery, Doc Library, Assessment Calendar but Request capabilities are hidden).
 * Includes: Sponsor Admin, Backend User, Backend Master User.
 * @param {string|null} userRole - Role from useUserRole() (e.g. "Sponsor User", "Backend Master User").
 * @param {string[]} subRoles - Array from user?.publicMetadata?.sub_role.
 * @returns {boolean}
 */
export const isAdminUser = (userRole, subRoles) => {
  if (userRole === "Backend User" || userRole === "Backend Master User") return true;
  if (userRole === "Sponsor Admin") return true;
  const roles = Array.isArray(subRoles) ? subRoles : [];
  return roles.some((r) => r === "Sponsor Admin" || (r && r.toLowerCase() === "sponsor admin"));
};

/**
 * Returns true only for Backend User and Backend Master User.
 * Use this when hiding Request capabilities (view-only): only these roles should have Document Library / Assessment Calendar / Discovery request actions hidden.
 * Sponsor Admin must remain able to make requests.
 */
export const isBackendViewOnlyUser = (userRole) => {
  return userRole === "Backend User" || userRole === "Backend Master User";
};

/**
 * Diligent Admin roles that can archive requests and see archive metadata (matches backend Authorize).
 */
export const isDiligentAdminUserRole = (userRole) =>
  userRole === "Backend User" || userRole === "Backend Master User";

/**
 * Returns the type of user based on the user role and sub roles.
 * @param {string} userRole - The user role from useUserRole() (e.g. "Sponsor User", "Backend Master User").
 * @param {string[]} subRoles - Array from user?.publicMetadata?.sub_role.
 * @returns {string} The type of user (e.g. "admin", "sponsor", "provider", "unknown").
 */
export const getUserType = (userRole, subRoles) => {
  if (userRole === "Backend User" || userRole === "Backend Master User") return "admin";
  if (userRole === "Sponsor User" || userRole === "Sponsor Master User") return "sponsor";
  if (userRole === "Provider User" || userRole === "Provider Master User") return "vendor";
  return "unknown";
};

// 13876 Autorelease — pre-acceptance statuses the FE checks before showing the panel.
// Mirrors BE _autoreleasePreAcceptanceStatuses (Sent / In Review). The action
// endpoint re-validates, so this is purely UI gating.
export const isPreAcceptanceStatus = (status) =>
  status === "Sent" || status === "In Review";

// 13876 Autorelease — audience gate. Vendor only for Phase 1.2; CRO-in-vendor-portal is
// explicitly deferred (no portal-context signal exists in Clerk metadata today).
export const isAutoreleaseAudience = (userRole, subRoles) => {
  return getUserType(userRole, subRoles) === "vendor";
};

const GXP_CODES_REGEX = /\b(GCP|GCLP|GLP|GMP|GVP|GDP|GAMP)\b/gi;

/**
 * Returns the GxP codes found in a VQQ/RFI title (de-duplicated, upper-cased).
 * Multi-GxP VQQs render one badge per match.
 */
export const extractGxpCodes = (name) => {
  if (!name || typeof name !== "string") return [];
  const seen = new Set();
  for (const match of name.matchAll(GXP_CODES_REGEX)) {
    seen.add(match[1].toUpperCase());
  }
  return Array.from(seen);
};

/**
 * Resolves the GxP code list for a VQQ details row. Prefers the structured API
 * field (which may already be comma- or slash-separated for multi-GxP) and falls
 * back to regex-extraction from the template name so legacy rows still get a badge.
 */
export const resolveGxpCodesFromRfi = (rfi) => {
  if (!rfi) return [];
  const apiField = rfi.gxp ?? rfi.gxp_label ?? rfi.gxpLabel ?? "";
  const apiCodes = extractGxpCodes(typeof apiField === "string" ? apiField : "");
  if (apiCodes.length > 0) return apiCodes;
  return extractGxpCodes(rfi.name ?? "");
};

/**
 * Removes GxP codes from a title and collapses leftover whitespace/separators
 * so the cleaned name reads naturally next to the rendered badges.
 */
export const stripGxpFromName = (name) => {
  if (!name || typeof name !== "string") return name ?? "";
  return name
    .replace(GXP_CODES_REGEX, "")
    .replace(/\s{2,}/g, " ")
    .replace(/\s*-\s*$/, "")
    .replace(/^\s*-\s*/, "")
    .trim();
};

// VQQ display-name helpers live in vqqNameFormat.js (dependency-free); re-exported
// here so existing helperMethods imports keep working.
export { formatVqqName, getTemplateNameOnly, appendVqqLocation } from "./vqqNameFormat";

const SECTION_CODE_REGEX = /\(([A-Z]{2,3})\)\s*$/;

export const extractSectionCode = (sectionName) => {
  if (!sectionName || typeof sectionName !== "string") return "??";
  const tail = sectionName.match(SECTION_CODE_REGEX);
  if (tail) return tail[1];
  const upper = sectionName.replace(/[^A-Z]/g, "");
  if (upper.length >= 2) return upper.slice(0, 2);
  const firstWord = sectionName.trim().split(/\s+/)[0] ?? "";
  return firstWord.slice(0, 2).toUpperCase() || "??";
};

export const stripSectionCode = (sectionName) => {
  if (!sectionName) return "";
  return sectionName.replace(SECTION_CODE_REGEX, "").trim();
};

export const useUserType = () => {
  const { user } = useUser();
  const role = useUserRole();
  const subRoles = user?.publicMetadata?.sub_role ?? [];
  return getUserType(role, subRoles);
};

/**
 * Returns the list of export option ids visible to the given user/status, or null if the
 * Export menu should be hidden entirely. Mirrors the BE ExportAuthorization gate used by
 * ExportQuestionnaire so FE and BE agree on who can do what (14537):
 *   - Sponsor: full menu (Responses + Documents) once the VQQ is submitted for review
 *     (In Review, In Remediation (Purchaser), Complete, Released); never partial.
 *   - Vendor / Diligent admin: full menu from In Review onward; partial Excel + PDF while the
 *     VQQ is still being worked (In Progress, In Remediation (Vendor)).
 * Status strings here come from SponsorRFIResponse.status (see StatusBadges.js).
 */
export const computeRfiExportOptions = (status, userRole, { showRiskAugmented = false } = {}) => {
  const userType = getUserType(userRole);
  const s = (status || "").trim();
  // Sent means the invite is out but the vendor has not accepted, so nothing exists to
  // export yet, partial included.
  const preAcceptance =
    s === "Pending" || s === "Sent" || s === "Rejected" || s === "Cancelled" || s === "";
  const isComplete = s === "Complete" || s === "Released";
  // Full responses + supporting documents exist once the vendor submits for review, so the full
  // download is offered from the initial submit (In Review) onward for every user type (14537):
  // In Review, In Remediation (Purchaser) (resubmitted, awaiting purchaser), and completion.
  // In Progress and In Remediation (Vendor) are vendor-editing states, so they stay partial-only
  // (vendor/admin) or hidden (sponsor). Kept in lockstep with the BE ExportAuthorization gate.
  const isSubmittedForReview =
    isComplete ||
    s === "In Review" ||
    s === "In Remediation (Purchaser)";

  // Six-entry full menu, in the order the export dropdown design specifies:
  //   ZIP_ALL → EXCEL_RESPONSES → (EXCEL_RISK_AUGMENTED) → PDF_DOCS →
  //   (PDF_RISK_AUGMENTED) → SUPPORTING_DOCS
  // The Risk-Augmented entries appear only when the caller signals risk
  // visibility (Complete + scoring enabled + scored + published for sponsors;
  // admin/vendor get more leeway — gating is enforced by the caller). The
  // entries are interleaved so the Excel pair stays together and the PDF
  // pair stays together — ExportMenu draws a divider only between groups,
  // so this ordering preserves the visual grouping shown in the design.
  const fullMenu = [
    "ZIP_ALL",
    "EXCEL_RESPONSES",
    ...(showRiskAugmented ? ["EXCEL_RISK_AUGMENTED"] : []),
    "PDF_DOCS",
    ...(showRiskAugmented ? ["PDF_RISK_AUGMENTED"] : []),
    "SUPPORTING_DOCS",
  ];
  const partialMenu = ["EXCEL_PARTIAL", "PDF_PARTIAL"];

  if (preAcceptance) return null;
  if (userType === "sponsor") return isSubmittedForReview ? fullMenu : null;
  if (userType === "vendor" || userType === "admin")
    return isSubmittedForReview ? fullMenu : partialMenu;
  return null;
};